import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { Wallet as WalletIcon, ArrowUpCircle, ArrowDownCircle } from "lucide-react";

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState({ balance: 0, transactions: [] });
  const [methods, setMethods] = useState([]);
  const [amount, setAmount] = useState(100);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const [w, m] = await Promise.all([
      api.get("/wallet"),
      api.get("/billing/methods").catch(() => ({ data: [] })),
    ]);
    setData(w.data);
    setMethods(m.data);
  }, []);

  const pollStatus = useCallback(async (session_id, attempts = 0) => {
    const max = 10;
    if (attempts >= max) { toast.message("Payment still processing — refresh in a minute"); return; }
    try {
      const { data: s } = await api.get(`/payments/status/${session_id}`);
      if (s.payment_status === "paid") {
        toast.success("Wallet topped up!");
        await refresh(); await load();
        window.history.replaceState({}, "", "/dashboard/wallet");
        return;
      }
      if (s.status === "expired") { toast.error("Checkout expired"); return; }
      setTimeout(() => pollStatus(session_id, attempts + 1), 2500);
    } catch (e) {
      // Silent retry - webhook path will credit wallet regardless
      setTimeout(() => pollStatus(session_id, attempts + 1), 2500);
    }
  }, [refresh, load]);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) pollStatus(sid);
  }, [load, pollStatus]);

  const topup = async () => {
    if (!amount || amount < 5) { toast.error("Minimum $5"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/topup", {
        amount: parseFloat(amount),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Top-up failed");
      setLoading(false);
    }
  };

  const chargeSaved = async () => {
    const defaultMethod = methods.find((m) => m.is_default && m.status === "active") || methods.find((m) => m.status === "active");
    if (!defaultMethod) { toast.error("Add a payment method first"); return; }
    if (!amount || amount < 5) { toast.error("Minimum $5"); return; }
    setLoading(true);
    try {
      await api.post("/wallet/charge-saved", { amount: parseFloat(amount), method_id: defaultMethod.method_id });
      toast.success(`Charged ${(defaultMethod.brand || "").toUpperCase()} ····${defaultMethod.last4} · $${amount}`);
      await refresh(); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Charge failed");
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Wallet</div>
        <h1 className="font-display text-3xl font-black tracking-tighter">Prepaid balance</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="md:col-span-1 brand-bg text-white rounded-sm p-6 relative overflow-hidden" data-testid="wallet-balance-card">
          <WalletIcon size={24} className="opacity-60" />
          <div className="text-xs uppercase tracking-[0.2em] opacity-80 mt-8">Current Balance</div>
          <div className="font-display text-5xl font-black tracking-tighter mt-2">
            ${(user?.wallet_balance || 0).toFixed(2)}
          </div>
          <div className="text-xs opacity-80 mt-2 font-mono-plex">{data.currency}</div>
        </div>
        <div className="md:col-span-2 bg-white border border-zinc-200 rounded-sm p-6">
          <div className="font-display text-xl font-bold mb-4">Top up wallet</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {[25, 100, 500, 1000].map((v) => (
              <button
                key={v}
                data-testid={`topup-preset-${v}`}
                onClick={() => setAmount(v)}
                className={`py-3 rounded-sm border ${amount == v ? "border-[var(--brand-accent)] bg-blue-50 brand-text" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"} font-display font-bold`}
              >
                ${v}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider text-zinc-500">Custom amount (USD)</label>
              <input
                data-testid="topup-amount-input"
                type="number" min="5" max="10000" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)]"
              />
            </div>
            <div className="flex gap-2">
              <button data-testid="topup-submit-btn" onClick={topup} disabled={loading}
                className="brand-bg text-white px-5 py-2.5 rounded-sm font-medium hover:opacity-90 disabled:opacity-50 text-sm">
                {loading ? "…" : "Checkout"}
              </button>
              {methods.length > 0 && (
                <button data-testid="charge-saved-btn" onClick={chargeSaved} disabled={loading}
                  className="border border-[var(--brand-accent)] brand-text px-5 py-2.5 rounded-sm font-medium hover:bg-blue-50 disabled:opacity-50 text-sm">
                  Charge default
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-3">
            {methods.length > 0
              ? `Default: ${(methods.find((m) => m.is_default) || methods[0])?.brand?.toUpperCase()} ····${(methods.find((m) => m.is_default) || methods[0])?.last4}. `
              : "Add a payment method to enable one-click top-ups. "}
            Funds are held in wallet and auto-debited when you book shipments.
          </p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="font-display text-xl font-bold mb-4">Transactions</div>
        {data.transactions.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 text-sm">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="text-left py-2">Date</th>
                <th className="text-left">Type</th>
                <th className="text-left">Description</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((t) => (
                <tr key={t.txn_id} className="border-b border-zinc-100" data-testid={`txn-${t.txn_id}`}>
                  <td className="py-2 font-mono-plex text-xs text-zinc-500">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td>
                    {t.type === "credit" || t.type === "refund" ? (
                      <span className="text-emerald-700 flex items-center gap-1 text-xs uppercase tracking-wider"><ArrowUpCircle size={12} /> {t.type}</span>
                    ) : (
                      <span className="text-red-700 flex items-center gap-1 text-xs uppercase tracking-wider"><ArrowDownCircle size={12} /> {t.type}</span>
                    )}
                  </td>
                  <td className="text-zinc-600">{t.description}</td>
                  <td className={`text-right font-mono-plex ${t.type === "debit" ? "text-red-700" : "text-emerald-700"}`}>
                    {t.type === "debit" ? "-" : "+"}${t.amount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
